/**
 * The native Google sign-in's device nonce (login CSRF on the deep link).
 *
 * The app signs in from the `nvcapp://localhost/auth/google/callback` deep
 * link, and any page, mail or message can open that link with a token of the
 * sender's choosing: before this, a link carrying an attacker's own token
 * signed the victim's device into the attacker's account, replacing whoever
 * was signed in. The web never takes a token from a URL (#1211, the state
 * cookie and the exchange code); the app cannot hold the system browser's
 * cookie, so it binds the deep link to itself instead:
 *
 *  1. Before opening the system browser, the app mints a random nonce, keeps
 *     it here (memory, plus localStorage so a process the OS killed while the
 *     browser tab was up still has it) and sends it as `app_nonce` to
 *     GET /auth/google, which folds it into the OAuth state, signed.
 *  2. The server's callback echoes it in the deep link as `an`.
 *  3. The app accepts a sign-in deep link only when `an` equals the nonce it
 *     holds, and spends it: the same link tapped again is refused.
 *
 * Ten minutes: the server's state TTL (AuthController::GOOGLE_STATE_TTL_SECONDS).
 */

export const GOOGLE_APP_NONCE_TTL_MS = 10 * 60 * 1000;

const STORAGE_KEY = 'google_signin_app_nonce';

type Pending = { nonce: string; exp: number };

let pending: Pending | null = null;

function readStored(): Pending | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Pending>;
    return typeof parsed.nonce === 'string' && typeof parsed.exp === 'number'
      ? { nonce: parsed.nonce, exp: parsed.exp }
      : null;
  } catch {
    return null;
  }
}

function clear(): void {
  pending = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable: the memory copy is gone, which is what matters.
  }
}

/** A fresh nonce for the sign-in about to open; replaces any earlier one. */
export function mintGoogleAppNonce(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  const nonce = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  pending = { nonce, exp: Date.now() + GOOGLE_APP_NONCE_TTL_MS };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
  } catch {
    // Memory alone still covers a sign-in the OS did not interrupt.
  }
  return nonce;
}

/**
 * True, once, when `an` is the nonce this device minted and it has not
 * expired. A missing or foreign `an` leaves the nonce in place, so a stray
 * link cannot cancel the sign-in the user is in the middle of.
 */
export function consumeGoogleAppNonce(an: string | null): boolean {
  const held = pending ?? readStored();
  if (held === null) return false;
  if (held.exp < Date.now()) {
    clear();
    return false;
  }
  if (!an || an !== held.nonce) return false;
  clear();
  return true;
}

/** The GET /auth/google URL that starts a sign-in; the app's carries a fresh nonce. */
export function googleOAuthStartUrl(apiUrl: string, state: string, isNativeApp: boolean): string {
  const url = `${apiUrl}/auth/google?state=${encodeURIComponent(state)}`;
  return isNativeApp ? `${url}&app_nonce=${mintGoogleAppNonce()}` : url;
}
